/** @typedef {{ skill: string, weight: number }} SkillWeight */

/** @param {[string, number][]} entries @returns {SkillWeight[]} */
function sw(entries) {
  return entries.map(([skill, weight]) => ({ skill, weight }));
}

/** Default skill weights keyed by role id (must total 100 per role). */
export const DEFAULT_ROLE_SKILL_WEIGHTS = {
  // ── IT ──────────────────────────────────────────────────────────────────
  'software-engineer': sw([
    ['Programming Fundamentals', 25], ['System Design', 20], ['Database & SQL', 18],
    ['API Development', 17], ['Testing & Debugging', 12], ['Communication', 8],
  ]),
  'senior-software-engineer': sw([
    ['System Design', 25], ['Technical Leadership', 20], ['Code Quality & Architecture', 20],
    ['Database & Performance', 15], ['Mentoring', 12], ['Stakeholder Communication', 8],
  ]),
  'qa-engineer': sw([
    ['Manual & Exploratory Testing', 25], ['Test Planning & Documentation', 20], ['Defect Tracking', 18],
    ['API / Integration Testing', 17], ['Agile & SDLC', 12], ['Communication', 8],
  ]),
  'test-automation-engineer': sw([
    ['Test Automation Frameworks', 28], ['Programming (Java/Python)', 22], ['CI/CD Integration', 18],
    ['API & UI Testing', 15], ['Performance Testing', 10], ['Reporting', 7],
  ]),
  'devops-engineer': sw([
    ['CI/CD Pipelines', 24], ['Kubernetes / Docker', 22], ['Cloud (AWS/Azure)', 20],
    ['Infrastructure as Code', 16], ['Monitoring', 10], ['Scripting', 8],
  ]),
  'cloud-engineer': sw([
    ['Cloud Architecture (AWS/Azure/GCP)', 28], ['Networking & Security', 20], ['IaC (Terraform/CloudFormation)', 18],
    ['Cost Optimization', 14], ['Migration & Hybrid Cloud', 12], ['Documentation', 8],
  ]),
  'site-reliability-engineer-sre': sw([
    ['Reliability Engineering', 25], ['Incident Management', 22], ['Observability & SLOs', 20],
    ['Automation & Scripting', 15], ['Capacity Planning', 12], ['Postmortem Culture', 6],
  ]),
  'data-engineer': sw([
    ['ETL / Data Pipelines', 26], ['SQL & Data Modeling', 22], ['Python / Spark', 20],
    ['Cloud Data Platforms', 15], ['Data Quality', 10], ['Orchestration (Airflow)', 7],
  ]),
  'data-scientist': sw([
    ['Python / R', 22], ['Machine Learning', 25], ['Statistics', 18],
    ['SQL & Data Pipelines', 15], ['Visualization', 10], ['Business Communication', 10],
  ]),
  'data-analyst': sw([
    ['SQL & Data Querying', 24], ['Data Analysis & Reporting', 22], ['Excel / BI Tools', 18],
    ['Data Visualization', 15], ['Business Context', 12], ['Communication', 9],
  ]),
  'ai-intern': sw([
    ['AI / ML Fundamentals', 30], ['Educational Background', 20],
    ['Academic / Project Experience', 25], ['Problem Solving & Learning Agility', 15],
    ['Communication Skills', 10],
  ]),
  'machine-learning-intern': sw([
    ['Machine Learning Fundamentals', 30], ['Educational Background', 20],
    ['Academic / Project Experience', 25], ['Problem Solving & Learning Agility', 15],
    ['Communication Skills', 10],
  ]),
  'data-analyst-intern': sw([
    ['SQL & Data Fundamentals', 28], ['Educational Background', 20],
    ['Academic / Project Experience', 25], ['Analytical Thinking', 15],
    ['Communication Skills', 12],
  ]),
  'java-intern': sw([
    ['Java / Programming Fundamentals', 30], ['Educational Background', 20],
    ['Academic / Project Experience', 25], ['Problem Solving', 15],
    ['Communication Skills', 10],
  ]),
  'python-intern': sw([
    ['Python Programming Fundamentals', 30], ['Educational Background', 20],
    ['Academic / Project Experience', 25], ['Problem Solving', 15],
    ['Communication Skills', 10],
  ]),
  'software-engineer-intern': sw([
    ['Programming Fundamentals', 30], ['Educational Background', 20],
    ['Academic / Project Experience', 25], ['Problem Solving', 15],
    ['Communication Skills', 10],
  ]),
  'solution-architect': sw([
    ['Enterprise Architecture', 25], ['Cloud & Integration Design', 22], ['Technical Leadership', 18],
    ['Security & Compliance', 15], ['Stakeholder Management', 12], ['Documentation', 8],
  ]),
  'business-analyst': sw([
    ['Requirements Gathering', 25], ['Process Modeling', 20], ['Stakeholder Communication', 18],
    ['Data Analysis', 15], ['Documentation (BRD/FRD)', 12], ['Agile / Scrum', 10],
  ]),
  'project-manager': sw([
    ['Project Planning & Scheduling', 25], ['Risk Management', 20], ['Stakeholder Management', 18],
    ['Budget & Resource Management', 15], ['Agile / Waterfall Methods', 12], ['Communication', 10],
  ]),
  'product-manager': sw([
    ['Product Strategy', 25], ['User Research & Roadmapping', 22], ['Agile Product Development', 18],
    ['Data-Driven Decision Making', 15], ['Cross-functional Leadership', 12], ['Communication', 8],
  ]),
  'cybersecurity-analyst': sw([
    ['Threat Detection', 25], ['SIEM Tools', 20], ['Network Security', 18],
    ['Incident Response', 17], ['Compliance (ISO/SOC)', 12], ['Reporting', 8],
  ]),
  'it-support-specialist': sw([
    ['Help Desk & Ticketing', 25], ['Hardware / Software Troubleshooting', 22], ['OS & Network Basics', 18],
    ['Active Directory / IAM', 15], ['Customer Service', 12], ['Documentation', 8],
  ]),

  // ── Healthcare ──────────────────────────────────────────────────────────
  'doctor-physician': sw([
    ['Clinical Diagnosis', 28], ['Patient Care', 22], ['Medical Knowledge', 20],
    ['Communication', 12], ['EMR Systems', 10], ['Ethics & Compliance', 8],
  ]),
  surgeon: sw([
    ['Surgical Procedures', 30], ['Anatomy & Pathology', 22], ['Operative Planning', 18],
    ['Patient Safety', 12], ['Team Coordination', 10], ['Post-operative Care', 8],
  ]),
  nurse: sw([
    ['Patient Care', 28], ['Clinical Assessment', 22], ['Medication Administration', 18],
    ['EMR Systems', 12], ['Communication', 12], ['Emergency Response', 8],
  ]),
  pharmacist: sw([
    ['Pharmacology', 30], ['Drug Interactions', 22], ['Patient Counseling', 18],
    ['Regulatory Compliance', 15], ['Inventory Management', 8], ['Documentation', 7],
  ]),
  'medical-laboratory-technician': sw([
    ['Lab Testing Procedures', 28], ['Sample Collection & Handling', 22], ['Quality Control', 18],
    ['Lab Equipment Operation', 15], ['Safety Protocols', 10], ['Documentation', 7],
  ]),
  radiologist: sw([
    ['Medical Imaging Interpretation', 30], ['Radiation Safety', 20], ['Diagnostic Accuracy', 20],
    ['PACS / RIS Systems', 12], ['Patient Communication', 10], ['Reporting', 8],
  ]),
  'clinical-research-associate': sw([
    ['Clinical Trial Protocols', 25], ['GCP Compliance', 22], ['Patient Recruitment', 18],
    ['Data Collection & CRF', 18], ['Site Monitoring', 10], ['Documentation', 7],
  ]),
  'healthcare-administrator': sw([
    ['Healthcare Operations', 25], ['Regulatory Compliance', 20], ['Budget Management', 18],
    ['Staff Management', 15], ['Quality Improvement', 12], ['Communication', 10],
  ]),
  'hospital-operations-manager': sw([
    ['Hospital Operations', 25], ['Resource Planning', 20], ['Patient Flow Management', 18],
    ['Quality & Safety Standards', 15], ['Vendor Management', 12], ['Leadership', 10],
  ]),
  'health-information-manager': sw([
    ['Health Information Systems', 25], ['HIPAA / Data Privacy', 22], ['Medical Records Management', 20],
    ['Coding Standards (ICD/CPT)', 15], ['Audit & Compliance', 10], ['Reporting', 8],
  ]),
  'medical-coder': sw([
    ['ICD-10 / CPT Coding', 30], ['Medical Terminology', 22], ['Claims Processing', 18],
    ['Compliance & Auditing', 15], ['EMR Systems', 10], ['Accuracy & Attention to Detail', 5],
  ]),
  'healthcare-data-analyst': sw([
    ['Healthcare Analytics', 25], ['SQL & BI Tools', 22], ['Clinical Data Standards', 18],
    ['Reporting & Dashboards', 15], ['HIPAA Compliance', 12], ['Communication', 8],
  ]),
  physiotherapist: sw([
    ['Physical Assessment', 28], ['Treatment Planning', 22], ['Therapeutic Exercise', 20],
    ['Patient Education', 12], ['Manual Therapy', 10], ['Documentation', 8],
  ]),
  'biomedical-engineer': sw([
    ['Medical Device Design', 25], ['Biomechanics', 20], ['Regulatory Standards (FDA/CE)', 18],
    ['Clinical Validation', 15], ['CAD / Prototyping', 12], ['Technical Documentation', 10],
  ]),
  'medical-device-specialist': sw([
    ['Medical Device Knowledge', 28], ['Installation & Calibration', 22], ['Troubleshooting', 18],
    ['Regulatory Compliance', 15], ['Clinical Training', 10], ['Customer Support', 7],
  ]),

  // ── Petroleum ─────────────────────────────────────────────────────────────
  geologist: sw([
    ['Geological Mapping', 28], ['Sedimentology & Stratigraphy', 22], ['Reservoir Characterization', 18],
    ['GIS & Geospatial Tools', 15], ['Field Investigation', 10], ['Technical Reporting', 7],
  ]),
  geophysicist: sw([
    ['Seismic Interpretation', 28], ['Geophysical Survey Methods', 22], ['Reservoir Modeling', 18],
    ['Data Processing Software', 15], ['Well Log Analysis', 10], ['Technical Reporting', 7],
  ]),
  'reservoir-engineer': sw([
    ['Reservoir Simulation', 28], ['Production Optimization', 22], ['Petroleum Geology', 18],
    ['EOR Techniques', 15], ['Data Analysis', 10], ['Technical Writing', 7],
  ]),
  'drilling-engineer': sw([
    ['Well Planning', 25], ['Drilling Fluids', 20], ['Rig Operations', 20],
    ['HSE Compliance', 18], ['Cost Control', 10], ['Team Leadership', 7],
  ]),
  'petroleum-engineer': sw([
    ['Production Engineering', 25], ['Well Completion Design', 22], ['Reservoir Performance', 18],
    ['Artificial Lift Systems', 15], ['HSE Compliance', 12], ['Technical Reporting', 8],
  ]),
  'production-engineer': sw([
    ['Production Optimization', 26], ['Well Performance Analysis', 22], ['Surface Facilities', 18],
    ['Process Safety', 15], ['Data Analysis', 12], ['Field Operations', 7],
  ]),
  'field-engineer': sw([
    ['Field Operations', 26], ['Equipment Troubleshooting', 22], ['Well Site Supervision', 18],
    ['HSE Compliance', 15], ['Reporting & Documentation', 12], ['Team Coordination', 7],
  ]),
  'refinery-operator': sw([
    ['Process Operations', 28], ['Control Room Monitoring', 22], ['Safety Procedures', 18],
    ['Equipment Operation', 15], ['Emergency Response', 10], ['Shift Handover', 7],
  ]),
  'petroleum-process-engineer': sw([
    ['Refinery Process Design', 25], ['Process Simulation', 22], ['Heat & Mass Transfer', 18],
    ['Safety & HAZOP', 15], ['Troubleshooting', 12], ['Technical Documentation', 8],
  ]),
  'pipeline-engineer': sw([
    ['Pipeline Design', 25], ['Hydraulics & Flow Assurance', 22], ['Integrity Management', 18],
    ['Corrosion Control', 15], ['Regulatory Compliance', 12], ['Project Coordination', 8],
  ]),
  'petroleum-maintenance-engineer': sw([
    ['Rotating Equipment Maintenance', 25], ['Preventive Maintenance Planning', 22], ['Reliability Engineering', 18],
    ['HSE Compliance', 15], ['Root Cause Analysis', 12], ['Vendor Management', 8],
  ]),
  'instrumentation-engineer': sw([
    ['Control Systems & PLCs', 26], ['Instrumentation Design', 22], ['Calibration & Testing', 18],
    ['DCS / SCADA', 15], ['Troubleshooting', 12], ['Safety Systems', 7],
  ]),
  'hse-officer': sw([
    ['HSE Regulations & Compliance', 28], ['Risk Assessment', 22], ['Incident Investigation', 18],
    ['Safety Training', 15], ['Environmental Management', 10], ['Reporting', 7],
  ]),
  'project-engineer': sw([
    ['Project Planning', 25], ['Engineering Design Review', 22], ['Cost & Schedule Control', 18],
    ['Contractor Management', 15], ['Technical Documentation', 12], ['HSE Compliance', 8],
  ]),
  'petroleum-operations-manager': sw([
    ['Operations Management', 25], ['Production Planning', 22], ['Budget & Cost Control', 18],
    ['HSE Leadership', 15], ['Team Management', 12], ['Stakeholder Communication', 8],
  ]),

  // ── Marine ──────────────────────────────────────────────────────────────
  'ship-captain': sw([
    ['Vessel Navigation', 28], ['Maritime Regulations (SOLAS/STCW)', 22], ['Crew Leadership', 18],
    ['Emergency Response', 15], ['Cargo Operations', 10], ['Communication', 7],
  ]),
  'deck-officer': sw([
    ['Bridge Operations', 28], ['Navigation & Chartwork', 22], ['Cargo Handling', 18],
    ['Safety Procedures', 15], ['Watchkeeping', 10], ['Communication', 7],
  ]),
  'navigation-officer': sw([
    ['Passage Planning', 28], ['ECDIS / Radar Operations', 22], ['COLREGs Compliance', 20],
    ['Weather Routing', 15], ['Voyage Documentation', 10], ['Communication', 5],
  ]),
  'marine-engineer': sw([
    ['Engine Room Operations', 26], ['Marine Propulsion', 22], ['Safety Regulations (SOLAS)', 20],
    ['Maintenance Planning', 16], ['Troubleshooting', 10], ['Logbook & Reporting', 6],
  ]),
  'chief-engineer': sw([
    ['Engine Room Supervision', 25], ['Crew Management', 20], ['Regulatory Compliance', 20],
    ['Budget & Procurement', 15], ['Emergency Procedures', 12], ['Technical Leadership', 8],
  ]),
  'port-manager': sw([
    ['Port Operations Management', 25], ['Berth & Terminal Planning', 22], ['Safety & Security', 18],
    ['Stakeholder Coordination', 15], ['Budget Management', 12], ['Regulatory Compliance', 8],
  ]),
  'harbor-master': sw([
    ['Vessel Traffic Management', 28], ['Port Safety Regulations', 22], ['Pilotage Coordination', 18],
    ['Emergency Response', 15], ['Environmental Compliance', 10], ['Communication', 7],
  ]),
  'shipyard-engineer': sw([
    ['Ship Construction', 25], ['Naval Architecture Basics', 22], ['Welding & Fabrication Standards', 18],
    ['Project Scheduling', 15], ['Quality Inspection', 12], ['Technical Documentation', 8],
  ]),
  'naval-architect': sw([
    ['Hull Design & Stability', 28], ['Hydrodynamics', 22], ['Structural Analysis', 18],
    ['CAD / Ship Design Software', 15], ['Classification Rules', 10], ['Technical Reporting', 7],
  ]),
  'marine-surveyor': sw([
    ['Vessel Inspection', 28], ['Classification Standards', 22], ['Damage Assessment', 18],
    ['Survey Reporting', 15], ['Regulatory Compliance', 10], ['Technical Knowledge', 7],
  ]),
  'marine-safety-officer': sw([
    ['Maritime Safety Regulations', 28], ['Risk Assessment', 22], ['Safety Audits', 18],
    ['Emergency Drills', 15], ['Incident Investigation', 10], ['Training & Awareness', 7],
  ]),
  'marine-fleet-manager': sw([
    ['Fleet Operations', 25], ['Vessel Scheduling', 22], ['Maintenance Planning', 18],
    ['Crew Management', 15], ['Cost Control', 12], ['Regulatory Compliance', 8],
  ]),
  'logistics-coordinator': sw([
    ['Shipment Coordination', 25], ['Documentation (B/L, Customs)', 22], ['Carrier Management', 18],
    ['Inventory Tracking', 15], ['ERP / TMS Systems', 12], ['Communication', 8],
  ]),
  'cargo-operations-manager': sw([
    ['Cargo Planning & Stowage', 25], ['Loading / Discharge Operations', 22], ['Safety & Compliance', 18],
    ['Team Supervision', 15], ['Cost Optimization', 12], ['Customer Coordination', 8],
  ]),
  'vessel-operations-executive': sw([
    ['Voyage Operations', 25], ['Charter Party Knowledge', 22], ['Port Agency Coordination', 18],
    ['Commercial Operations', 15], ['Documentation', 12], ['Communication', 8],
  ]),

  // ── Manufacturing ───────────────────────────────────────────────────────
  'production-manager': sw([
    ['Production Planning', 25], ['Team Leadership', 22], ['Quality Management', 18],
    ['Lean Manufacturing', 15], ['Budget & Cost Control', 12], ['Safety Compliance', 8],
  ]),
  'production-supervisor': sw([
    ['Shop Floor Supervision', 28], ['Production Scheduling', 22], ['Quality Control', 18],
    ['Safety Procedures', 15], ['Team Communication', 10], ['Problem Solving', 7],
  ]),
  'plant-manager': sw([
    ['Plant Operations', 25], ['Strategic Planning', 22], ['P&L Management', 18],
    ['Continuous Improvement', 15], ['Regulatory Compliance', 12], ['Leadership', 8],
  ]),
  'manufacturing-engineer': sw([
    ['Manufacturing Processes', 25], ['CAD / CAM', 22], ['Process Optimization', 18],
    ['Quality Engineering', 15], ['Lean / Six Sigma', 12], ['Technical Documentation', 8],
  ]),
  'industrial-engineer': sw([
    ['Process Improvement', 25], ['Workflow Design', 22], ['Ergonomics & Layout', 18],
    ['Data Analysis', 15], ['Lean / Six Sigma', 12], ['Project Management', 8],
  ]),
  'manufacturing-process-engineer': sw([
    ['Process Design & Validation', 25], ['Statistical Process Control', 22], ['Equipment Specification', 18],
    ['Root Cause Analysis', 15], ['Documentation (SOPs)', 12], ['Cross-functional Collaboration', 8],
  ]),
  'lean-manufacturing-specialist': sw([
    ['Lean Methodologies (5S, Kaizen)', 28], ['Value Stream Mapping', 22], ['Waste Reduction', 18],
    ['Change Management', 15], ['Training & Coaching', 10], ['Data Analysis', 7],
  ]),
  'quality-engineer': sw([
    ['Quality Standards (ISO)', 25], ['Inspection & Testing', 22], ['Root Cause Analysis', 18],
    ['SOP Documentation', 15], ['Statistical Process Control', 12], ['Cross-functional Coordination', 8],
  ]),
  'quality-assurance-manager': sw([
    ['QA System Management', 25], ['Audit & Compliance', 22], ['CAPA Management', 18],
    ['Team Leadership', 15], ['Supplier Quality', 12], ['Continuous Improvement', 8],
  ]),
  'manufacturing-maintenance-engineer': sw([
    ['Preventive Maintenance', 25], ['Equipment Reliability', 22], ['CMMS Systems', 18],
    ['Root Cause Analysis', 15], ['Safety Compliance', 12], ['Vendor Management', 8],
  ]),
  'automation-engineer': sw([
    ['PLC Programming', 26], ['Industrial Automation', 22], ['Robotics Integration', 18],
    ['SCADA / HMI', 15], ['Troubleshooting', 12], ['Safety Systems', 7],
  ]),
  'manufacturing-supply-chain-manager': sw([
    ['Supply Chain Planning', 25], ['Vendor Management', 22], ['Inventory Optimization', 18],
    ['ERP Systems', 15], ['Cost Reduction', 12], ['Negotiation', 8],
  ]),
  'procurement-specialist': sw([
    ['Sourcing & Procurement', 28], ['Vendor Negotiation', 22], ['Contract Management', 18],
    ['Cost Analysis', 15], ['ERP / Procurement Systems', 10], ['Compliance', 7],
  ]),
  'manufacturing-warehouse-manager': sw([
    ['Warehouse Operations', 25], ['Inventory Management', 22], ['WMS Systems', 18],
    ['Team Supervision', 15], ['Safety & Compliance', 12], ['Process Improvement', 8],
  ]),
  'manufacturing-operations-manager': sw([
    ['Operations Management', 25], ['KPI & Performance Metrics', 22], ['Resource Planning', 18],
    ['Continuous Improvement', 15], ['Budget Management', 12], ['Leadership', 8],
  ]),

  // ── Banking ─────────────────────────────────────────────────────────────
  'bank-manager': sw([
    ['Branch Management', 25], ['Financial Products Knowledge', 22], ['Sales & Revenue Targets', 18],
    ['Risk & Compliance', 15], ['Team Leadership', 12], ['Customer Service', 8],
  ]),
  'branch-operations-manager': sw([
    ['Branch Operations', 28], ['Process Compliance', 22], ['Cash & Vault Management', 18],
    ['Staff Scheduling', 15], ['Customer Issue Resolution', 10], ['Reporting', 7],
  ]),
  'relationship-manager': sw([
    ['Customer Relationship', 28], ['Financial Products', 22], ['Sales & Cross-selling', 18],
    ['KYC / Compliance', 15], ['CRM Tools', 10], ['Negotiation', 7],
  ]),
  'loan-officer': sw([
    ['Credit Assessment', 28], ['Loan Products Knowledge', 22], ['Underwriting', 18],
    ['Regulatory Compliance', 15], ['Customer Communication', 10], ['Documentation', 7],
  ]),
  'corporate-banking-officer': sw([
    ['Corporate Lending', 25], ['Financial Statement Analysis', 22], ['Relationship Management', 18],
    ['Risk Assessment', 15], ['Deal Structuring', 12], ['Regulatory Knowledge', 8],
  ]),
  'investment-banker': sw([
    ['Financial Modeling', 28], ['M&A / Capital Markets', 22], ['Valuation', 18],
    ['Due Diligence', 15], ['Client Presentation', 10], ['Negotiation', 7],
  ]),
  'wealth-manager': sw([
    ['Portfolio Management', 25], ['Financial Planning', 22], ['Client Relationship', 18],
    ['Investment Products', 15], ['Risk Profiling', 12], ['Regulatory Compliance', 8],
  ]),
  'portfolio-manager': sw([
    ['Portfolio Construction', 28], ['Asset Allocation', 22], ['Risk Management', 18],
    ['Market Analysis', 15], ['Performance Reporting', 10], ['Compliance', 7],
  ]),
  'financial-analyst': sw([
    ['Financial Modeling', 28], ['Excel & BI Tools', 22], ['Financial Statement Analysis', 18],
    ['Forecasting & Budgeting', 15], ['Reporting', 10], ['Communication', 7],
  ]),
  'treasury-analyst': sw([
    ['Cash Management', 25], ['Liquidity Planning', 22], ['FX & Hedging', 18],
    ['Banking Relationships', 15], ['Treasury Systems', 12], ['Risk & Compliance', 8],
  ]),
  'risk-analyst': sw([
    ['Risk Assessment', 28], ['Credit / Market Risk Models', 22], ['Regulatory Frameworks', 18],
    ['Data Analysis', 15], ['Reporting', 10], ['Stakeholder Communication', 7],
  ]),
  'compliance-officer': sw([
    ['Regulatory Compliance', 28], ['Policy Development', 22], ['Audit & Monitoring', 18],
    ['AML / KYC', 15], ['Reporting', 10], ['Training & Awareness', 7],
  ]),
  'fraud-analyst': sw([
    ['Fraud Detection', 28], ['Transaction Monitoring', 22], ['Investigation', 18],
    ['AML Tools & Systems', 15], ['Pattern Analysis', 10], ['Reporting', 7],
  ]),
  'credit-analyst': sw([
    ['Credit Risk Assessment', 28], ['Financial Statement Analysis', 22], ['Underwriting', 18],
    ['Industry Research', 15], ['Credit Scoring Models', 10], ['Documentation', 7],
  ]),
  'internal-auditor': sw([
    ['Audit Planning', 25], ['Risk-Based Auditing', 22], ['Internal Controls', 18],
    ['Regulatory Standards', 15], ['Reporting & Findings', 12], ['Communication', 8],
  ]),

  // ── Aviation ────────────────────────────────────────────────────────────
  pilot: sw([
    ['Flight Operations', 30], ['Navigation & Weather', 22], ['Safety Procedures', 20],
    ['Aircraft Systems', 15], ['Crew Resource Management', 8], ['Regulatory Compliance', 5],
  ]),
  'first-officer': sw([
    ['Flight Operations Support', 28], ['Navigation & Systems', 22], ['Emergency Procedures', 18],
    ['Crew Coordination', 15], ['Regulatory Compliance', 12], ['Communication', 5],
  ]),
  'flight-dispatcher': sw([
    ['Flight Planning', 28], ['Weather Analysis', 22], ['Fuel & Weight Calculations', 18],
    ['Regulatory Compliance', 15], ['Communication Systems', 10], ['Crisis Management', 7],
  ]),
  'chief-pilot': sw([
    ['Flight Operations Leadership', 25], ['Crew Management', 22], ['Safety Management', 20],
    ['Training & Standards', 15], ['Regulatory Compliance', 12], ['Scheduling', 6],
  ]),
  'cabin-crew': sw([
    ['Passenger Safety', 28], ['In-flight Service', 22], ['Emergency Procedures', 20],
    ['First Aid', 15], ['Communication', 10], ['Teamwork', 5],
  ]),
  'air-traffic-controller': sw([
    ['Airspace Management', 30], ['Radar & Navigation Systems', 22], ['Communication', 18],
    ['Emergency Response', 15], ['Regulatory Compliance', 10], ['Stress Management', 5],
  ]),
  'aircraft-maintenance-engineer': sw([
    ['Aircraft Systems Maintenance', 28], ['AMM / Technical Manuals', 22], ['Troubleshooting', 18],
    ['Regulatory Compliance (FAA/EASA)', 15], ['Safety Procedures', 10], ['Documentation', 7],
  ]),
  'aircraft-inspector': sw([
    ['Airworthiness Inspection', 28], ['Regulatory Standards', 22], ['Non-destructive Testing', 18],
    ['Audit & Compliance', 15], ['Technical Reporting', 10], ['Safety Assessment', 7],
  ]),
  'technical-services-engineer': sw([
    ['Aircraft Technical Support', 25], ['Maintenance Planning', 22], ['Engineering Analysis', 18],
    ['Regulatory Documentation', 15], ['Fleet Reliability', 12], ['Vendor Coordination', 8],
  ]),
  'ground-operations-executive': sw([
    ['Ground Handling Operations', 25], ['Turnaround Coordination', 22], ['Safety & Security', 18],
    ['Resource Scheduling', 15], ['Customer Service', 12], ['Communication', 8],
  ]),
  'airport-manager': sw([
    ['Airport Operations', 25], ['Safety & Security Management', 22], ['Stakeholder Coordination', 18],
    ['Budget & Revenue', 15], ['Regulatory Compliance', 12], ['Crisis Management', 8],
  ]),
  'aviation-safety-officer': sw([
    ['Safety Management Systems', 28], ['Risk Assessment', 22], ['Incident Investigation', 18],
    ['Regulatory Compliance', 15], ['Safety Audits', 10], ['Training', 7],
  ]),
  'aviation-security-specialist': sw([
    ['Aviation Security Regulations', 28], ['Threat Assessment', 22], ['Access Control', 18],
    ['Screening Procedures', 15], ['Emergency Response', 10], ['Reporting', 7],
  ]),
  'aerospace-engineer': sw([
    ['Aerospace Design', 28], ['Aerodynamics & Structures', 22], ['CAD / Simulation Tools', 18],
    ['Materials & Manufacturing', 15], ['Testing & Validation', 10], ['Technical Documentation', 7],
  ]),
  'flight-operations-manager': sw([
    ['Flight Operations Management', 25], ['Crew Scheduling', 22], ['Safety & Compliance', 18],
    ['Performance Metrics', 15], ['Budget Management', 12], ['Stakeholder Communication', 8],
  ]),

  // ── Education ───────────────────────────────────────────────────────────
  teacher: sw([
    ['Subject Knowledge', 28], ['Lesson Planning', 22], ['Classroom Management', 18],
    ['Student Assessment', 15], ['Communication', 10], ['Parent Engagement', 7],
  ]),
  lecturer: sw([
    ['Subject Expertise', 28], ['Curriculum Delivery', 22], ['Research Integration', 18],
    ['Student Mentoring', 15], ['Presentation Skills', 10], ['Academic Writing', 7],
  ]),
  professor: sw([
    ['Research & Publications', 28], ['Advanced Subject Knowledge', 22], ['Graduate Teaching', 18],
    ['Academic Leadership', 15], ['Grant Writing', 10], ['Mentoring', 7],
  ]),
  principal: sw([
    ['School Leadership', 25], ['Academic Standards', 22], ['Staff Management', 18],
    ['Budget & Administration', 15], ['Community Relations', 12], ['Policy Compliance', 8],
  ]),
  dean: sw([
    ['Academic Administration', 25], ['Faculty Management', 22], ['Strategic Planning', 18],
    ['Accreditation & Compliance', 15], ['Budget Management', 12], ['Stakeholder Relations', 8],
  ]),
  'academic-coordinator': sw([
    ['Curriculum Coordination', 25], ['Faculty Scheduling', 22], ['Academic Quality', 18],
    ['Student Support', 15], ['Reporting & Documentation', 12], ['Communication', 8],
  ]),
  'curriculum-developer': sw([
    ['Curriculum Design', 28], ['Learning Objectives', 22], ['Assessment Design', 18],
    ['Educational Standards', 15], ['Content Development', 10], ['Stakeholder Collaboration', 7],
  ]),
  'instructional-designer': sw([
    ['Instructional Design Models', 28], ['E-Learning Development', 22], ['Learning Technology', 18],
    ['Content Creation', 15], ['Needs Analysis', 10], ['Evaluation Methods', 7],
  ]),
  'educational-technologist': sw([
    ['EdTech Tools & LMS', 28], ['Digital Content Development', 22], ['Technology Integration', 18],
    ['Faculty Training', 15], ['Technical Support', 10], ['Innovation', 7],
  ]),
  'education-counselor': sw([
    ['Student Counseling', 28], ['Career Guidance', 22], ['Academic Advising', 18],
    ['Crisis Intervention', 15], ['Communication', 10], ['Record Keeping', 7],
  ]),
  'admissions-officer': sw([
    ['Admissions Process', 25], ['Student Recruitment', 22], ['Application Evaluation', 18],
    ['CRM / Admissions Systems', 15], ['Communication', 12], ['Event Management', 8],
  ]),
  'school-administrator': sw([
    ['School Administration', 25], ['Policy Implementation', 22], ['Staff Coordination', 18],
    ['Budget & Resources', 15], ['Compliance', 12], ['Communication', 8],
  ]),
  'training-manager': sw([
    ['Training Program Design', 25], ['Needs Assessment', 22], ['Facilitator Management', 18],
    ['LMS Administration', 15], ['Evaluation & ROI', 12], ['Budget Management', 8],
  ]),
  'learning-development-specialist': sw([
    ['Learning Design', 25], ['Facilitation & Delivery', 22], ['Adult Learning Principles', 18],
    ['Content Development', 15], ['Assessment Methods', 12], ['Stakeholder Engagement', 8],
  ]),
  'research-associate': sw([
    ['Research Methodology', 28], ['Data Collection & Analysis', 22], ['Literature Review', 18],
    ['Academic Writing', 15], ['Lab / Field Techniques', 10], ['Ethics & Compliance', 7],
  ]),

  // ── Agriculture ───────────────────────────────────────────────────────────
  'farm-manager': sw([
    ['Farm Operations Management', 25], ['Crop & Livestock Planning', 22], ['Budget & Cost Control', 18],
    ['Equipment Management', 15], ['Safety & Compliance', 12], ['Team Leadership', 8],
  ]),
  'farm-supervisor': sw([
    ['Field Supervision', 28], ['Crop Operations', 22], ['Worker Safety', 18],
    ['Equipment Operation', 15], ['Quality Control', 10], ['Reporting', 7],
  ]),
  'crop-production-manager': sw([
    ['Crop Planning & Rotation', 25], ['Irrigation Management', 22], ['Pest & Disease Control', 18],
    ['Yield Optimization', 15], ['Budget Management', 12], ['Team Supervision', 8],
  ]),
  agronomist: sw([
    ['Crop Science', 28], ['Soil & Nutrient Management', 22], ['Pest Management', 18],
    ['Field Trials & Research', 15], ['Advisory & Reporting', 10], ['Sustainability Practices', 7],
  ]),
  'agricultural-engineer': sw([
    ['Farm Machinery Design', 25], ['Irrigation Systems', 22], ['Soil & Water Conservation', 18],
    ['CAD / Technical Design', 15], ['Project Management', 12], ['Safety Standards', 8],
  ]),
  'irrigation-specialist': sw([
    ['Irrigation System Design', 28], ['Water Management', 22], ['Pump & Pipeline Systems', 18],
    ['Soil Moisture Analysis', 15], ['Maintenance & Troubleshooting', 10], ['Sustainability', 7],
  ]),
  'precision-agriculture-specialist': sw([
    ['GPS / GIS Technology', 28], ['Remote Sensing & Drones', 22], ['Variable Rate Technology', 18],
    ['Data Analytics', 15], ['Farm Management Software', 10], ['Advisory', 7],
  ]),
  'agricultural-scientist': sw([
    ['Agricultural Research', 28], ['Crop & Soil Science', 22], ['Experimental Design', 18],
    ['Data Analysis', 15], ['Technical Reporting', 10], ['Field Trials', 7],
  ]),
  'soil-scientist': sw([
    ['Soil Analysis', 28], ['Soil Chemistry & Fertility', 22], ['Land Use Planning', 18],
    ['Environmental Impact', 15], ['Lab Techniques', 10], ['Technical Reporting', 7],
  ]),
  horticulturist: sw([
    ['Plant Cultivation', 28], ['Greenhouse Management', 22], ['Pest & Disease Control', 18],
    ['Landscape Design', 15], ['Propagation Techniques', 10], ['Advisory', 7],
  ]),
  'livestock-manager': sw([
    ['Animal Husbandry', 28], ['Feed & Nutrition Management', 22], ['Health & Biosecurity', 18],
    ['Breeding Programs', 15], ['Facility Management', 10], ['Record Keeping', 7],
  ]),
  'food-safety-officer': sw([
    ['Food Safety Standards (HACCP)', 28], ['Inspection & Auditing', 22], ['Hygiene Compliance', 18],
    ['Risk Assessment', 15], ['Training & Awareness', 10], ['Documentation', 7],
  ]),
  'agricultural-consultant': sw([
    ['Agronomic Advisory', 25], ['Farm Business Planning', 22], ['Crop & Livestock Expertise', 18],
    ['Market Analysis', 15], ['Sustainability Practices', 12], ['Communication', 8],
  ]),
  'agricultural-extension-officer': sw([
    ['Farmer Training & Outreach', 28], ['Agricultural Best Practices', 22], ['Field Demonstrations', 18],
    ['Program Implementation', 15], ['Communication', 10], ['Reporting', 7],
  ]),
  'agri-business-manager': sw([
    ['Agribusiness Operations', 25], ['Supply Chain & Marketing', 22], ['Financial Management', 18],
    ['Market Analysis', 15], ['Stakeholder Management', 12], ['Strategic Planning', 8],
  ]),

  // ── Logistics ───────────────────────────────────────────────────────────
  'logistics-supply-chain-manager': sw([
    ['Supply Chain Strategy', 25], ['Vendor Management', 22], ['Demand & Inventory Planning', 18],
    ['ERP / SCM Systems', 15], ['Cost Optimization', 12], ['Leadership', 8],
  ]),
  'supply-chain-analyst': sw([
    ['Demand Planning', 24], ['Inventory Optimization', 22], ['ERP / WMS Systems', 18],
    ['Data Analysis', 16], ['Vendor Management', 12], ['Communication', 8],
  ]),
  'logistics-manager': sw([
    ['Logistics Operations', 25], ['Transportation Planning', 22], ['Warehouse Coordination', 18],
    ['Cost Management', 15], ['KPI & Performance', 12], ['Team Leadership', 8],
  ]),
  'logistics-analyst': sw([
    ['Logistics Data Analysis', 25], ['Route Optimization', 22], ['Cost Modeling', 18],
    ['TMS / WMS Systems', 15], ['Reporting & Dashboards', 12], ['Process Improvement', 8],
  ]),
  'operations-coordinator': sw([
    ['Operations Coordination', 25], ['Scheduling & Dispatch', 22], ['Documentation', 18],
    ['ERP / TMS Systems', 15], ['Problem Solving', 12], ['Communication', 8],
  ]),
  'logistics-warehouse-manager': sw([
    ['Warehouse Operations', 25], ['Inventory Control', 22], ['WMS Systems', 18],
    ['Team Supervision', 15], ['Safety & Compliance', 12], ['Process Improvement', 8],
  ]),
  'transportation-manager': sw([
    ['Transportation Planning', 25], ['Fleet Coordination', 22], ['Route Optimization', 18],
    ['Carrier Management', 15], ['Cost Control', 12], ['Regulatory Compliance', 8],
  ]),
  'logistics-fleet-manager': sw([
    ['Fleet Management', 25], ['Vehicle Maintenance Planning', 22], ['Driver Management', 18],
    ['GPS / Telematics', 15], ['Cost Optimization', 12], ['Safety Compliance', 8],
  ]),
  'procurement-manager': sw([
    ['Strategic Sourcing', 25], ['Vendor Negotiation', 22], ['Contract Management', 18],
    ['Spend Analysis', 15], ['ERP / Procurement Systems', 12], ['Team Leadership', 8],
  ]),
  'inventory-analyst': sw([
    ['Inventory Analysis', 28], ['Forecasting & Replenishment', 22], ['ERP / WMS Systems', 18],
    ['Data Reporting', 15], ['Process Optimization', 10], ['Communication', 7],
  ]),
  'distribution-manager': sw([
    ['Distribution Network Management', 25], ['Warehouse & Transport Coordination', 22], ['Order Fulfillment', 18],
    ['KPI Management', 15], ['Cost Control', 12], ['Team Leadership', 8],
  ]),
  'shipping-coordinator': sw([
    ['Shipment Coordination', 25], ['Freight Documentation', 22], ['Carrier Liaison', 18],
    ['Customs Basics', 15], ['Tracking & Visibility', 12], ['Communication', 8],
  ]),
  'import-export-specialist': sw([
    ['Import / Export Regulations', 28], ['Customs Documentation', 22], ['Trade Compliance', 18],
    ['Incoterms & Shipping Terms', 15], ['Freight Coordination', 10], ['Communication', 7],
  ]),
  'demand-planner': sw([
    ['Demand Forecasting', 28], ['Statistical Modeling', 22], ['S&OP Process', 18],
    ['ERP / Planning Tools', 15], ['Cross-functional Collaboration', 10], ['Reporting', 7],
  ]),
  'fulfillment-manager': sw([
    ['Order Fulfillment Operations', 25], ['Warehouse & Pick-Pack Coordination', 22], ['SLA Management', 18],
    ['Process Optimization', 15], ['Team Leadership', 12], ['Customer Satisfaction', 8],
  ]),

  // ── Hotel Management ──────────────────────────────────────────────────────
  'hotel-manager': sw([
    ['Hotel Operations Management', 25], ['Guest Experience', 22], ['Revenue & Budget Management', 18],
    ['Team Leadership', 15], ['Health & Safety Compliance', 12], ['Stakeholder Communication', 8],
  ]),
  'assistant-hotel-manager': sw([
    ['Operations Support', 25], ['Guest Service Standards', 22], ['Staff Coordination', 18],
    ['Shift Management', 15], ['Problem Resolution', 12], ['Reporting', 8],
  ]),
  'front-office-executive': sw([
    ['Front Desk Operations', 28], ['Guest Check-in / Check-out', 22], ['Reservation Systems (PMS)', 18],
    ['Customer Service', 15], ['Billing & Payments', 10], ['Communication', 7],
  ]),
  'guest-relations-executive': sw([
    ['Guest Relationship Management', 28], ['Complaint Handling', 22], ['VIP Guest Services', 18],
    ['Communication', 15], ['Hospitality Standards', 10], ['Conflict Resolution', 7],
  ]),
  'housekeeping-manager': sw([
    ['Housekeeping Operations', 28], ['Quality & Inspection Standards', 22], ['Staff Scheduling', 18],
    ['Inventory & Linen Management', 15], ['Safety & Hygiene Compliance', 10], ['Training', 7],
  ]),
  'restaurant-manager': sw([
    ['Restaurant Operations', 25], ['Team Leadership', 22], ['Customer Service', 18],
    ['Food Cost Control', 15], ['Health & Safety Standards', 12], ['Revenue Management', 8],
  ]),
  'food-beverage-manager': sw([
    ['F&B Operations Management', 25], ['Menu Planning & Costing', 22], ['Vendor Management', 18],
    ['Quality Standards', 15], ['Team Supervision', 12], ['Guest Satisfaction', 8],
  ]),
  chef: sw([
    ['Culinary Skills', 30], ['Menu Development', 22], ['Kitchen Operations', 18],
    ['Food Safety & Hygiene', 15], ['Cost Control', 10], ['Team Leadership', 5],
  ]),
  'banquet-manager': sw([
    ['Banquet Operations', 28], ['Event Setup & Coordination', 22], ['Client Communication', 18],
    ['Staff Management', 15], ['Food Service Standards', 10], ['Budget Control', 7],
  ]),
  'event-coordinator': sw([
    ['Event Planning', 28], ['Vendor Coordination', 22], ['Client Communication', 18],
    ['Timeline Management', 15], ['On-site Execution', 10], ['Problem Solving', 7],
  ]),
  'reservations-executive': sw([
    ['Reservation Management', 28], ['PMS / CRS Systems', 22], ['Rate & Availability Management', 18],
    ['Customer Communication', 15], ['Upselling', 10], ['Accuracy & Attention to Detail', 7],
  ]),
  'revenue-manager': sw([
    ['Revenue Management Strategy', 28], ['Pricing & Yield Optimization', 22], ['Forecasting & Analytics', 18],
    ['PMS / RMS Tools', 15], ['Market Analysis', 10], ['Stakeholder Reporting', 7],
  ]),

  // ── Railways ──────────────────────────────────────────────────────────────
  'station-manager': sw([
    ['Station Operations', 28], ['Passenger Service Management', 22], ['Safety & Security Compliance', 18],
    ['Staff Supervision', 15], ['Incident Management', 10], ['Reporting', 7],
  ]),
  'railway-operations-executive': sw([
    ['Rail Operations Coordination', 25], ['Schedule Adherence', 22], ['Safety Procedures', 18],
    ['Communication Systems', 15], ['Incident Reporting', 12], ['Documentation', 8],
  ]),
  'traffic-controller': sw([
    ['Traffic Control Operations', 28], ['Signalling Coordination', 22], ['Safety Regulations', 18],
    ['Real-time Decision Making', 15], ['Communication', 10], ['Emergency Response', 7],
  ]),
  'railway-engineer': sw([
    ['Railway Engineering', 28], ['Infrastructure Design', 22], ['Track & Systems Knowledge', 18],
    ['Safety Standards', 15], ['Project Management', 10], ['Technical Documentation', 7],
  ]),
  'signalling-engineer': sw([
    ['Signalling Systems', 28], ['Interlocking & Control Systems', 22], ['Installation & Testing', 18],
    ['Safety Compliance', 15], ['Troubleshooting', 10], ['Technical Documentation', 7],
  ]),
  'railway-safety-officer': sw([
    ['Rail Safety Regulations', 28], ['Risk Assessment', 22], ['Incident Investigation', 18],
    ['Safety Audits', 15], ['Training & Awareness', 10], ['Reporting', 7],
  ]),
  'train-operations-manager': sw([
    ['Train Operations Management', 25], ['Crew & Schedule Coordination', 22], ['Safety Compliance', 18],
    ['Performance Metrics', 15], ['Stakeholder Communication', 12], ['Crisis Management', 8],
  ]),
  'railway-maintenance-engineer': sw([
    ['Track & Rolling Stock Maintenance', 28], ['Preventive Maintenance Planning', 22], ['Fault Diagnosis', 18],
    ['Safety Procedures', 15], ['Technical Documentation', 10], ['Vendor Coordination', 7],
  ]),
  'railway-logistics-coordinator': sw([
    ['Freight Coordination', 25], ['Shipment Scheduling', 22], ['Documentation', 18],
    ['Carrier Liaison', 15], ['Compliance', 12], ['Communication', 8],
  ]),

  // ── Sales & Marketing ────────────────────────────────────────────────────
  'sales-executive': sw([
    ['Sales Closing & Negotiation', 28], ['Product Knowledge', 20], ['Lead Generation', 18],
    ['CRM Tools', 14], ['Communication', 12], ['Target Achievement', 8],
  ]),
  'sales-manager': sw([
    ['Sales Strategy', 26], ['Team Leadership', 22], ['Pipeline Management', 18],
    ['Client Relationship', 14], ['Forecasting', 12], ['Coaching', 8],
  ]),
  'sales-representative': sw([
    ['Prospecting', 26], ['Product Demo', 20], ['Objection Handling', 18],
    ['CRM Usage', 14], ['Communication', 14], ['Follow-up Discipline', 8],
  ]),
  'inside-sales-executive': sw([
    ['Outbound Calling', 26], ['Lead Qualification', 22], ['CRM Tools', 16],
    ['Product Knowledge', 16], ['Communication', 12], ['Conversion Rate', 8],
  ]),
  'field-sales-executive': sw([
    ['Field Sales Execution', 28], ['Territory Management', 20], ['Client Meetings', 18],
    ['Product Knowledge', 14], ['Reporting', 12], ['Travel Readiness', 8],
  ]),
  'regional-sales-manager': sw([
    ['Regional Sales Strategy', 26], ['Team Leadership', 22], ['Market Expansion', 18],
    ['Key Account Management', 14], ['Forecasting', 12], ['Stakeholder Management', 8],
  ]),
  'business-development-executive': sw([
    ['Lead Generation', 26], ['Market Research', 20], ['Pitching', 18],
    ['Networking', 14], ['CRM Tools', 12], ['Communication', 10],
  ]),
  'business-development-manager': sw([
    ['Business Strategy', 26], ['Partnership Development', 22], ['Pipeline Growth', 18],
    ['Negotiation', 14], ['Market Analysis', 12], ['Leadership', 8],
  ]),
  'key-account-manager': sw([
    ['Account Growth', 28], ['Relationship Management', 22], ['Upsell / Cross-sell', 18],
    ['Contract Negotiation', 14], ['CRM & Reporting', 10], ['Communication', 8],
  ]),
  'marketing-executive': sw([
    ['Campaign Execution', 26], ['Content Creation', 20], ['Market Research', 16],
    ['Digital Channels', 16], ['Analytics', 12], ['Communication', 10],
  ]),
  'marketing-manager': sw([
    ['Marketing Strategy', 26], ['Brand Management', 20], ['Campaign Planning', 18],
    ['Team Leadership', 14], ['Budget Management', 12], ['Analytics', 10],
  ]),
  'digital-marketing-specialist': sw([
    ['SEO / SEM', 26], ['Social Media Marketing', 20], ['Paid Ads', 18],
    ['Analytics', 14], ['Content Strategy', 12], ['A/B Testing', 10],
  ]),
  'brand-manager': sw([
    ['Brand Strategy', 28], ['Market Positioning', 22], ['Campaign Oversight', 16],
    ['Consumer Insights', 14], ['Cross-functional Collaboration', 12], ['Creative Direction', 8],
  ]),
  'customer-success-manager': sw([
    ['Customer Retention', 28], ['Onboarding', 20], ['Account Health Monitoring', 18],
    ['Upsell Opportunities', 14], ['Communication', 12], ['Product Knowledge', 8],
  ]),
  'account-manager': sw([
    ['Account Relationship', 28], ['Revenue Growth', 20], ['Issue Resolution', 16],
    ['Contract Management', 14], ['CRM', 12], ['Communication', 10],
  ]),
  'client-relationship-manager': sw([
    ['Client Relationship', 28], ['Service Delivery', 20], ['Issue Escalation', 16],
    ['Upselling', 14], ['Communication', 12], ['Reporting', 10],
  ]),
  'pre-sales-consultant': sw([
    ['Solution Demo', 26], ['Requirement Gathering', 22], ['Technical Presentation', 18],
    ['Proposal Writing', 14], ['Product Knowledge', 12], ['Communication', 8],
  ]),
  'solution-consultant': sw([
    ['Solution Design', 28], ['Client Discovery', 20], ['Technical Demo', 18],
    ['Proposal Support', 14], ['Product Expertise', 12], ['Communication', 8],
  ]),

  // ── Human Resources ──────────────────────────────────────────────────────
  recruiter: sw([
    ['Sourcing', 26], ['Screening & Interviewing', 22], ['Candidate Experience', 16],
    ['ATS Tools', 14], ['Negotiation', 12], ['Communication', 10],
  ]),
  'talent-acquisition-specialist': sw([
    ['Talent Sourcing', 26], ['Interview Coordination', 20], ['Employer Branding', 16],
    ['ATS / CRM', 14], ['Stakeholder Management', 14], ['Communication', 10],
  ]),
  'sourcing-specialist': sw([
    ['Boolean Search', 28], ['Pipeline Building', 22], ['Candidate Outreach', 18],
    ['ATS Tools', 14], ['Market Mapping', 10], ['Communication', 8],
  ]),
  'hr-executive': sw([
    ['HR Operations', 26], ['Employee Onboarding', 20], ['Policy Compliance', 16],
    ['Employee Relations', 14], ['HRIS', 14], ['Communication', 10],
  ]),
  'hr-manager': sw([
    ['HR Strategy', 24], ['People Management', 22], ['Policy & Compliance', 18],
    ['Performance Management', 14], ['Stakeholder Management', 12], ['Communication', 10],
  ]),
  'hr-business-partner': sw([
    ['Strategic Partnership', 26], ['Workforce Planning', 20], ['Change Management', 18],
    ['Employee Relations', 14], ['Performance Coaching', 12], ['Communication', 10],
  ]),
  'l-d-specialist': sw([
    ['Training Design', 26], ['Learning Programs', 22], ['Needs Assessment', 16],
    ['Facilitation', 14], ['LMS Tools', 12], ['Evaluation', 10],
  ]),
  'training-coordinator': sw([
    ['Training Coordination', 28], ['Scheduling', 20], ['Vendor Management', 16],
    ['Documentation', 14], ['Participant Engagement', 12], ['Reporting', 10],
  ]),
  'compensation-analyst': sw([
    ['Compensation Analysis', 28], ['Market Benchmarking', 22], ['Salary Structures', 18],
    ['Excel / Analytics', 14], ['Compliance', 10], ['Reporting', 8],
  ]),
  'payroll-specialist': sw([
    ['Payroll Processing', 30], ['Statutory Compliance', 22], ['Payroll Systems', 18],
    ['Data Accuracy', 14], ['Employee Queries', 10], ['Reporting', 6],
  ]),

  // ── Finance & Accounting ─────────────────────────────────────────────────
  accountant: sw([
    ['Financial Accounting', 28], ['Bookkeeping', 20], ['Reconciliation', 18],
    ['Tax Basics', 14], ['ERP / Tally', 12], ['Reporting', 8],
  ]),
  'accounts-executive': sw([
    ['Accounts Payable / Receivable', 26], ['Invoice Processing', 22], ['Reconciliation', 18],
    ['ERP Tools', 14], ['Documentation', 12], ['Accuracy', 8],
  ]),
  'senior-accountant': sw([
    ['Financial Statements', 26], ['Audit Support', 20], ['GAAP / IFRS', 18],
    ['Team Oversight', 14], ['ERP Systems', 12], ['Analysis', 10],
  ]),
  'finance-manager': sw([
    ['Financial Planning', 26], ['Budget Management', 22], ['Reporting & Analysis', 18],
    ['Team Leadership', 14], ['Compliance', 12], ['Stakeholder Communication', 8],
  ]),
  'fp-a-analyst': sw([
    ['Financial Modeling', 28], ['Forecasting', 22], ['Variance Analysis', 18],
    ['Excel / BI Tools', 14], ['Business Partnering', 10], ['Reporting', 8],
  ]),
  'tax-consultant': sw([
    ['Tax Advisory', 28], ['Compliance Filing', 22], ['Tax Planning', 18],
    ['Regulatory Knowledge', 14], ['Client Communication', 10], ['Documentation', 8],
  ]),
  'tax-analyst': sw([
    ['Tax Computation', 28], ['Return Filing', 22], ['Compliance', 18],
    ['Research', 14], ['Documentation', 10], ['ERP Tools', 8],
  ]),
  'accounts-payable-executive': sw([
    ['Invoice Processing', 28], ['Vendor Management', 22], ['Payment Scheduling', 18],
    ['ERP Tools', 14], ['Reconciliation', 10], ['Accuracy', 8],
  ]),
  'accounts-receivable-executive': sw([
    ['Collections', 28], ['Invoice Tracking', 22], ['Customer Follow-up', 18],
    ['Reconciliation', 14], ['ERP Tools', 10], ['Reporting', 8],
  ]),
};
